import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getIdleMs, idleState, markActivity, readLastActivity } from '../utils/idleSession';

// Real user input. Background polling and status checks are deliberately NOT
// in this list. (mousemove is handled separately below.)
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'touchstart', 'wheel', 'scroll', 'click'];

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Signs the user out after 15 minutes without activity, with a warning during
 * the last minute. Render once, inside <AuthProvider> (and the router).
 *
 * "Last activity" is shared through localStorage, so activity in any open tab
 * keeps every tab signed in, and an idle one signs out all of them.
 */
export function IdleTimeout() {
  const { isAuthenticated, endSession, logout } = useAuth();
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  // Latest endSession without re-running the effect on every render.
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;
  const warningRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      warningRef.current = false;
      setRemainingMs(null);
      return;
    }

    let finished = false;

    const check = () => {
      if (finished) return;
      const last = readLastActivity();
      const now = Date.now();
      const state = idleState(last, now);
      if (state === 'expired') {
        finished = true;
        warningRef.current = false;
        setRemainingMs(null);
        void endSessionRef.current(last === null ? 'expired' : 'idle');
      } else if (state === 'warning' && last !== null) {
        warningRef.current = true;
        setRemainingMs(Math.max(0, last + getIdleMs() - now));
      } else if (warningRef.current) {
        // activity in another tab (or the button) cleared the warning
        warningRef.current = false;
        setRemainingMs(null);
      }
    };

    // While the warning is showing, only the "Stay signed in" button counts, so
    // a brushed mouse or scroll wheel cannot dismiss it unnoticed.
    const onActivity = () => {
      if (warningRef.current) return;
      markActivity();
    };

    // Browsers fire synthetic mousemove events after layout changes even though
    // nobody touched the mouse; only count movement that changes position.
    let lastX = -1;
    let lastY = -1;
    const onMouseMove = (e: MouseEvent) => {
      if (e.screenX === lastX && e.screenY === lastY) return;
      lastX = e.screenX;
      lastY = e.screenY;
      onActivity();
    };

    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { capture: true, passive: true }));
    window.addEventListener('mousemove', onMouseMove, { capture: true, passive: true });
    // Re-check at once when the tab wakes up or another tab changes the record.
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    window.addEventListener('storage', check);
    const timer = window.setInterval(check, 1000);
    check();

    return () => {
      finished = true;
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity, { capture: true } as EventListenerOptions));
      window.removeEventListener('mousemove', onMouseMove, { capture: true } as EventListenerOptions);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      window.removeEventListener('storage', check);
      window.clearInterval(timer);
    };
  }, [isAuthenticated]);

  const stay = () => {
    markActivity(Date.now(), true);
    warningRef.current = false;
    setRemainingMs(null);
  };

  if (!isAuthenticated || remainingMs === null) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 px-4"
      style={{ zIndex: 1000 }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-title"
      aria-describedby="idle-desc"
    >
      <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 shadow-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <Clock className="w-5 h-5 text-amber-600" />
          <h2 id="idle-title" className="text-base font-semibold text-gray-900">Are you still there?</h2>
        </div>
        <p id="idle-desc" className="text-sm text-gray-600">
          For your security you will be signed out in{' '}
          <span className="font-semibold text-gray-900 tabular-nums">{formatCountdown(remainingMs)}</span>{' '}
          because of inactivity.
        </p>
        <div className="mt-5 flex space-x-3">
          <button
            type="button"
            autoFocus
            onClick={stay}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Stay signed in
          </button>
          <button
            type="button"
            onClick={() => { void logout(); }}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
